// Cierre de la apertura de cuenta: la cuenta quedó lista y las salidas del vendedor
// (ir al portal, abrir otra cuenta o venderle un teléfono al mismo cliente).
import { useNavigate } from '@tanstack/react-router';
import { Check, Home, Plus, Smartphone } from 'lucide-react';
import { Button, Loading } from '@/kit';
import { useWizardSession } from '../api/useWizard';
import {
    ACCOUNT_OPENED_EYEBROW,
    ACCOUNT_OPENED_HEADLINE,
    ACCOUNT_OPENED_OPEN_ANOTHER,
    ACCOUNT_OPENED_PHONE_PREFIX,
    ACCOUNT_OPENED_SELL_PHONE,
    ACCOUNT_OPENED_TITLE,
    ACCOUNT_OPENED_TO_PORTAL,
} from '../accountOpening';
import { formatPhone } from '../phoneFormat';
import { StateIcon } from '../components/KycScreenBits';
import { Confetti } from '../components/Confetti';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardFootNav } from '../components/WizardFootNav';

export function AccountOpenedStep({ sessionId }: { sessionId: string }) {
    const navigate = useNavigate();
    const { data: session } = useWizardSession(sessionId);
    if (!session) return <Loading size="lg" />;

    const customer = session.kyc.verified?.fullName;

    return (
        // `isolate` encierra el confeti detrás de la tarjeta, igual que el cierre de la venta.
        <div className="isolate w-full max-w-[680px] lg:max-w-[720px]">
            <Confetti pieces={140} origin="behind" />
            <WizardCard>
                <WizardCardHead
                    eyebrow={ACCOUNT_OPENED_EYEBROW}
                    title={ACCOUNT_OPENED_TITLE}
                    sub={`${ACCOUNT_OPENED_PHONE_PREFIX} ${formatPhone(session.phone)}`}
                />

                <WizardCardBody>
                    <div className="text-center">
                        <StateIcon tone="success">
                            <Check className="size-7" strokeWidth={3} aria-hidden />
                        </StateIcon>
                        <div className="text-[16px] font-semibold">{ACCOUNT_OPENED_HEADLINE}</div>
                        {customer && (
                            <p className="mt-0.5 text-[12.5px] text-muted-foreground">{customer}</p>
                        )}
                    </div>
                </WizardCardBody>

                <WizardFootNav
                    showExit={false}
                    back={
                        <Button
                            variant="ghost"
                            leftIcon={<Home className="size-3.5" aria-hidden />}
                            onClick={() => void navigate({ to: '/sales' })}
                        >
                            {ACCOUNT_OPENED_TO_PORTAL}
                        </Button>
                    }
                >
                    <Button
                        variant="outline"
                        leftIcon={<Plus className="size-3.5" aria-hidden />}
                        onClick={() =>
                            void navigate({ to: '/wizard', search: { accountOpening: true } })
                        }
                    >
                        {ACCOUNT_OPENED_OPEN_ANOTHER}
                    </Button>
                    <Button
                        leftIcon={<Smartphone className="size-3.5" aria-hidden />}
                        onClick={() =>
                            void navigate({ to: '/wizard', search: { phone: session.phone } })
                        }
                    >
                        {ACCOUNT_OPENED_SELL_PHONE}
                    </Button>
                </WizardFootNav>
            </WizardCard>
        </div>
    );
}
